import React, {useState} from "react";
import {
  useTable,
  usePagination,
  useGlobalFilter,
  useSortBy,
  useRowSelect,
} from "react-table";
import {
  Table,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  TableContainer,
  Button,
} from "@mui/material";
import Empty from "../error/Empty";
import TableFooter from "./sub-components/TableFooter";
import TableHeader from "./sub-components/TableHeader";
import TableFeature from "./sub-components/TableFeature";
import TableCustomizerDialog from "./sub-components/TableCustomizerDialog";
import {exportData, getExportableData} from "../../manager/dataManager";
import TableLoader from "../TableLoader";
import {forecastDefaultColumn} from "./cell/forecastTableCustomCell";
import TableFilterPopOver from "./sub-components/TableFilterPopOver";
import TableHeaderLayout from "./sub-components/TableHeaderLayout";
import TableCheckbox from "./sub-components/TableCheckbox";
import {Download} from "@mui/icons-material";

function ForecastOrderTable({
  columns,
  data,
  fromDate,
  toDate,
  handleFromDate,
  handleToDate,
  callAPI,
  loading,
  overDueData,
}) {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    nextPage,
    previousPage,
    canNextPage,
    pageOptions,
    state,
    setPageSize,
    canPreviousPage,
    setGlobalFilter,
    prepareRow,
    allColumns,
    getToggleHideAllColumnsProps,
    selectedFlatRows,
    gotoPage,
    pageCount,
  } = useTable(
    {
      columns,
      data,
      defaultColumn: forecastDefaultColumn,
      initialState: {
        pageSize: 15,
        hiddenColumns: columns
          .filter((col) => col.show === false)
          .map((col) => col.accessor),
      },
    },
    useGlobalFilter,
    useSortBy,
    usePagination,
    useRowSelect,
    (hooks) => {
      hooks.visibleColumns.push((columns) => {
        return [
          {
            id: "selection",
            Header: ({getToggleAllRowsSelectedProps}) => (
              <TableCheckbox {...getToggleAllRowsSelectedProps()} />
            ),
            Cell: ({row}) => (
              <TableCheckbox {...row.getToggleRowSelectedProps()} />
            ),
          },
          ...columns,
        ];
      });
    }
  );

  const {pageIndex, pageSize, globalFilter} = state;
  const [anchorEl, setAnchorEl] = useState(null);
  const [showTableCustomizer, setShowTableCustomizer] = useState(false);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <TableCustomizerDialog
        open={showTableCustomizer}
        handleClose={() => setShowTableCustomizer(false)}
        allColumns={allColumns}
        getToggleHideAllColumnsProps={getToggleHideAllColumnsProps}
      />
      <TableFilterPopOver
        open={open}
        handleClose={handleClose}
        anchorEl={anchorEl}
        fromDate={fromDate}
        toDate={toDate}
        callAPI={callAPI}
        handleFromDate={handleFromDate}
        handleToDate={handleToDate}
      />
      <TableHeaderLayout
        setGlobalFilter={setGlobalFilter}
        globalFilter={globalFilter}
      >
        {selectedFlatRows.length === 0 ? (
          <TableFeature
            handleFilter={handleClick}
            openCustomizer={() => setShowTableCustomizer(true)}
            refresh={callAPI}
            exportHandler={() =>
              exportData(getExportableData(data, columns), `Forecast Order ${fromDate} to ${toDate}`)
            }
          />
        ) : (
          <Button
            variant="outlined"
            endIcon={<Download />}
            onClick={() => {
              exportData(getExportableData(selectedFlatRows.map((row) => row.original), columns),
                `Purchase Order ${fromDate} to ${toDate}`
              );
            }}
          >
            Export Selected
          </Button>
        )}
      </TableHeaderLayout>
      {data.length === 0 && loading === false && (
        <Empty title={"No data found"} />
      )}
      {data.length !== 0 && loading === false && (
        <>
          <TableContainer component={Paper}>
            <Table
              {...getTableProps()}
              sx={{
                borderCollapse: "separate",
                minWidth: "max-content",
              }}
            >
              <TableHeader headerGroups={headerGroups} />
              <TableBody {...getTableBodyProps()}>
                {page.map((row) => {
                  prepareRow(row);
                  return (
                    <TableRow {...row.getRowProps()}>
                      {row.cells.map((cell) => {
                        return (
                          <TableCell {...cell.getCellProps()}>
                            {cell.render("Cell")}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TableFooter
            canNextPage={canNextPage}
            canPreviousPage={canPreviousPage}
            pageOptions={pageOptions}
            pageIndex={pageIndex}
            nextPage={nextPage}
            previousPage={previousPage}
            setPageSize={setPageSize}
            pageSize={pageSize}
            gotoPage={gotoPage}
            pageCount={pageCount}
          />
        </>
      )}
      {loading === true && <TableLoader title={"Loading..."} />}
    </>
  );
}

export default ForecastOrderTable;
